import { useState } from "react";

import { useApiClient } from "@/src/hooks/useApiClient";
import { useAppToast } from "@/src/hooks/useAppToast";
import { useTranslations } from "@/src/i18n/i18n";
import type { Report } from "@/src/pages/Reports/reports.type";
import { sanctionTarget } from "@/src/pages/Reports/sanction";

/**
 * Supprime pour de bon le contenu visé par un signalement.
 *
 * **C'est l'autre moitié de la modération**, celle que le verdict ne fait pas :
 * `PATCH /moderation/reports/:id` n'écrit qu'un statut et une trace d'examen,
 * tandis que ces routes-ci retirent réellement le contenu. Les deux sont
 * indépendantes — on peut supprimer sans clore, et clore sans supprimer.
 *
 * ⚠️ **`sanctioned` ne survit pas au rechargement**, et c'est assumé : le
 * serveur ne renvoie nulle part « ce signalement a donné lieu à une
 * suppression ». Cet ensemble n'existe donc que pour empêcher le second clic
 * de la même session, qui ne récolterait qu'un 404. Le jour où le contrat
 * portera cette information, elle remplacera cet état local.
 */
export function useSanction() {
  const $api = useApiClient();
  const t = useTranslations("reports");
  const { showSuccess, showApiError } = useAppToast();

  const [sanctioningId, setSanctioningId] = useState<number>();
  const [sanctioned, setSanctioned] = useState<ReadonlySet<number>>(
    () => new Set(),
  );

  const deleteMessage = $api.useMutation(
    "delete",
    "/api/v1/conversation/message/{messageId}",
  );
  const deleteEvent = $api.useMutation("delete", "/api/v1/event");
  const deleteAccount = $api.useMutation(
    "delete",
    "/api/v1/account/delete/{id}",
  );
  const deletePost = $api.useMutation("delete", "/api/v1/moderation/post/{id}");
  const deleteComment = $api.useMutation(
    "delete",
    "/api/v1/moderation/post/comment/{commentId}",
  );

  const sanction = (report: Report) => {
    const target = sanctionTarget(report);
    if (!target) return;

    setSanctioningId(report.id);

    /*
      Les callbacks sont passés **à l'appel**, pas au hook : c'est ici qu'on
      sait de quel signalement il s'agit, information que la mutation ignore
      (elle ne connaît que l'identifiant de la cible). Ce sont bien les
      `onSuccess` / `onError` de React Query, pas un effet sur `isSuccess`.
    */
    const handlers = {
      onSuccess: () => {
        setSanctioned((done) => new Set(done).add(report.id));
        showSuccess(t("sanction-done"));
      },
      onError: (error: unknown) => showApiError(t("sanction-error"), error),
      onSettled: () => setSanctioningId(undefined),
    };

    switch (target.kind) {
      case "message":
        deleteMessage.mutate(
          { params: { path: { messageId: target.id } } },
          handlers,
        );
        return;

      case "account":
        deleteAccount.mutate({ params: { path: { id: target.id } } }, handlers);
        return;

      // L'événement se désigne par son corps, pas par son chemin.
      case "event":
        deleteEvent.mutate({ body: { id: target.id } }, handlers);
        return;

      /*
        Fil d'actualité. Ces deux routes effacent **définitivement** — cascades
        et objets S3 compris —, là où la suppression par un auteur est douce et
        laisse la ligne lisible pour la modération.
      */
      case "post":
        deletePost.mutate({ params: { path: { id: target.id } } }, handlers);
        return;

      case "comment":
        deleteComment.mutate(
          { params: { path: { commentId: target.id } } },
          handlers,
        );
        return;
    }
  };

  return { sanction, sanctioningId, sanctioned };
}
